import React, { useEffect, useRef, useState } from "react";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as String from "../commons/string";

import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { useAlert } from "../provider/AlertProvider";
import { ColumnsType, ColumnType } from "antd/lib/table";
import { Button, Col, Divider, Input, InputRef, Space } from "antd";
import { FilterConfirmProps } from "antd/lib/table/interface";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import DataTable from "../components/DataTable";

type DataIndex = keyof Types.EventPrizeDto;

function EventPrizeList({ eventId }: { eventId: number }) {
    const alert = useAlert();

    const [eventPrizes, setEventPrizes] = useState<Array<Types.EventPrizeDto>>([]);

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex, subDataIndex?: keyof Types.EventUserDto): ColumnType<Types.EventPrizeDto> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        검색
                    </Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        초기화
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        필터
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        닫기
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) => {
            let fieldData = record[dataIndex];
            if (subDataIndex !== undefined) {
                const subFieldData = fieldData as Types.EventUserDto;
                if (subFieldData === null) return false;
                fieldData = subFieldData[subDataIndex] as string | number | boolean;
            }

            if (fieldData === null || fieldData === undefined) return false;

            return fieldData
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase());
        },
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<Types.EventPrizeDto> = [
        {
            align: "center",
            title: "ID",
            dataIndex: "eventPrizeId",
            key: "eventPrizeId",
            ...getColumnSearchProps("eventPrizeId"),
        },
        {
            align: "center",
            title: "상품명",
            dataIndex: "prizeName",
            key: "prizeName",
            ...getColumnSearchProps("prizeName"),
        },
        {
            align: "center",
            title: "할당",
            dataIndex: "selected",
            key: "selected",
            ...getColumnSearchProps("selected"),
            render: (_, { selected }) => {
                return selected ? "할당" : String.dash;
            },
        },
        {
            align: "center",
            title: "할당일자",
            dataIndex: "selectedDate",
            key: "selectedDate",
            ...getColumnSearchProps("selectedDate"),
        },
        {
            align: "center",
            title: "수령",
            dataIndex: "received",
            key: "received",
            ...getColumnSearchProps("received"),
            render: (_, { received }) => {
                return received ? "수령" : String.dash;
            },
        },
        {
            align: "center",
            title: "수령일자",
            dataIndex: "receivedDate",
            key: "receivedDate",
            ...getColumnSearchProps("receivedDate"),
        },
        {
            align: "center",
            title: "수령인",
            dataIndex: ["eventUser", "name"],
            key: "name",
            ...getColumnSearchProps("eventUser", "name"), // TODO:
        },
        {
            align: "center",
            title: "전화번호",
            dataIndex: ["eventUser", "phone"],
            key: "phone",
            ...getColumnSearchProps("eventUser", "phone"), // TODO:
        },
    ];

    useEffect(() => {
        setEventPrizes([]);

        requestGetEventPrizes();
    }, [eventId]);

    const requestAxiosGetEventPrizes = async () => {
        let response = await utilAxiosWithAuth().get(Request.EVENT_URL + eventId + Request.EVENT_PRIZES_URL);
        return response.data;
    };

    const {
        loading: loadingGetEventPrizes,
        error: errorGetEventPrizes,
        data: resultGetEventPrizes,
        execute: requestGetEventPrizes,
    } = useAsyncAxios(requestAxiosGetEventPrizes);

    useEffect(() => {
        if (resultGetEventPrizes === null) return;
        console.log("resultGetEventPrizes", resultGetEventPrizes);
        setEventPrizes(resultGetEventPrizes.eventPrizes);
    }, [resultGetEventPrizes]);

    useEffect(() => {
        if (errorGetEventPrizes === null) return;

        console.log("errorGetEventPrizes", errorGetEventPrizes);
        alert.setAlert(errorGetEventPrizes.response.data.code, "Error", errorGetEventPrizes.response.data.message);
    }, [errorGetEventPrizes]);

    return (
        <>
            <Col span={6}>
                <Divider orientation="left">
                    <span>상품</span>
                </Divider>
            </Col>
            <DataTable hasScroll={false} columns={columns} rowKey="eventPrizeId" dataSource={eventPrizes} />
        </>
    );
}

export default EventPrizeList;
